import { useCallback, useEffect, useRef, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { type User } from '@/@types/global';
import { modelsTogglesAtom } from '@/modules/annotator/findingSidebar/atoms';
import {
  previewDisplayDeviceAtom,
  previewModeAtom,
  showSegmentationAtom,
} from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import { PatientEducationContent } from '@/modules/annotator/patientEducationDialog/PatientEducationContent';
import { useGetModelFeatureFlags } from '@/modules/annotator/utils';
import { useSearchParams } from '@/utils/hooks/useSearchParams';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { ExternalPatientContext, isValidModalType } from '../constants';
import { ExternalError } from '../ExternalError';
import { useGetTrialStatus } from '../hooks';
import type { ModalCloseMessage, ModalOpenMessage } from '../types';
import { IframeMessageType, ModalType } from '../types';
import { useExternalPatientData } from '../useExternalPatientData';

import { AIImpact } from './AIImpact';
import { ExternalModalLayout } from './ExternalModalLayout';
import { ExternalQRPrintDownload } from './ExternalQRPrintDownload';
import { Onboarding } from './Onboarding';
import { Upgrade } from './Upgrade';

export const ExternalModalHandler = () => {
  const { trackEvent } = useTrackEvent();
  const location = useLocation();
  const history = useHistory();

  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );

  const searchParams = useSearchParams();
  const _modalType = searchParams.get('modal') as ModalType | null;
  const [modalType, setModalType] = useState<ModalType | null>(_modalType);

  const modalStartTime = useRef<Date | null>(null);
  const [externalPatientId, setExternalPatientId] = useState<string | null>(
    null,
  );
  const [externalImageId, setExternalImageId] = useState<string | null>(null);

  const setPreviewMode = useSetAtom(previewModeAtom);
  const setPreviewDisplayDevice = useSetAtom(previewDisplayDeviceAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const setEnabledFindingTypes = useSetAtom(modelsTogglesAtom);
  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();
  const { t } = useLingui();

  const {
    patientIdData,
    imageIdData,
    isLoadingPatientData,
    isErrorPatientData,
    visitSummaries,
    isLoadingVisitAndImageData,
    isErrorVisitAndImageData,
    isSuccess,
  } = useExternalPatientData({
    externalPatientId,
    externalImageId,
    enabled: true,
  });

  const {
    data: trialStatusData,
    isLoading: isLoadingTrialStatus,
    isError: isErrorTrialStatus,
  } = useGetTrialStatus();

  const trackModalDuration = useCallback(() => {
    if (!modalStartTime.current || !modalType) return;

    const duration = (Date.now() - modalStartTime.current.getTime()) / 1000; // duration in seconds
    trackEvent(mixpanelEvents.externalModalDuration, {
      modal: modalType,
      duration,
      externalPatientId,
      externalImageId,
      patientId: patientIdData?.patientId ?? null,
      imageId: imageIdData?.videaImageId ?? null,
      trialStatus: trialStatusData?.status ?? null,
      centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      isIframe: true,
    });
  }, [
    trackEvent,
    externalPatientId,
    externalImageId,
    patientIdData?.patientId,
    imageIdData?.videaImageId,
    modalType,
    trialStatusData?.status,
    trialStatusData?.centralizedBilling,
  ]);

  const isDataLoaded = isSuccess && !!trialStatusData;

  useEffect(() => {
    if (
      user?.practice && // we want the PMS type to be present, so have to make sure that practice is present
      modalType &&
      (isDataLoaded ||
        isErrorPatientData ||
        isErrorVisitAndImageData ||
        isErrorTrialStatus)
    ) {
      trackEvent(mixpanelEvents.externalPmsEntry, {
        source: 'modal',
        modal: modalType,
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
        trialStatus: trialStatusData?.status ?? null,
        centralizedBilling: trialStatusData?.centralizedBilling ?? null,
        isIframe: true,
      });
    }
    // Disabling exhaustive deps so that event is only tracked once each time new modal content is populated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modalType,
    isDataLoaded,
    isErrorPatientData,
    isErrorVisitAndImageData,
    isErrorTrialStatus,
    user?.practice,
  ]);

  // Listen for postMessage events to dynamically update modal type and handle close events
  // TODO: is there a way for us to verify the origin of the message?
  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<ModalOpenMessage | ModalCloseMessage>,
    ) => {
      const eventType = event.data?.type;

      if (eventType === IframeMessageType.MODAL_OPEN) {
        const payload = (event.data as ModalOpenMessage).payload;
        const newModalType = payload?.type;
        if (isValidModalType(newModalType)) {
          setModalType(newModalType);
          setExternalPatientId(payload?.externalPatientId);
          setExternalImageId(payload?.externalImageId);
          modalStartTime.current = new Date(); // Start tracking modal duration

          // TODO: All of the below params logic is temporary for testing
          // Update URL parameters without causing a full page reload
          const newUrlParams = new URLSearchParams(location.search);
          newUrlParams.set('modal', newModalType);

          // Update the URL without triggering a navigation
          history.replace({
            pathname: location.pathname,
            search: newUrlParams.toString(),
          });
        } else {
          console.warn(`Unsupported modal type received: ${newModalType}`);
        }
      } else if (eventType === IframeMessageType.MODAL_CLOSE) {
        trackModalDuration();
        modalStartTime.current = null;
        // Close the modal and reset the location
        setModalType(null);
        history.replace({
          pathname: location.pathname,
          search: '',
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    history,
    location.pathname,
    location.search,
    setExternalImageId,
    setExternalPatientId,
    trackModalDuration,
  ]);

  useEffect(() => {
    if (
      modalType === ModalType.PRINT_DOWNLOAD ||
      modalType === ModalType.QR_CODE
    ) {
      // Set finding types to the default feature flag values for
      // QR/print/download sidebar configurations
      setEnabledFindingTypes({
        caries: true,
        incipients: true,
        user: true,
        periapical_radiolucency: isPrlEnabled,
        radiographic_boneloss: isRblEnabled,
        calculus: isCalculusEnabled,
        area_of_concern: isCariesSubclassEnabled,
        restorative_adjacent_lucency: isVdaEnabled,
      });

      if (modalType === ModalType.PRINT_DOWNLOAD) {
        setShowSegmentation(true);
        setPreviewMode('print');
        setPreviewDisplayDevice('desktop');
      } else if (modalType === ModalType.QR_CODE) {
        setShowSegmentation(true);
        setPreviewMode('qrCode');
        setPreviewDisplayDevice('mobile');
      }
    }
  }, [
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isPrlEnabled,
    isRblEnabled,
    isVdaEnabled,
    modalType,
    setEnabledFindingTypes,
    setPreviewDisplayDevice,
    setPreviewMode,
    setShowSegmentation,
  ]);

  const modalView = () => {
    if (modalType && (!externalPatientId || !externalImageId)) {
      return (
        <ExternalModalLayout className="vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`We didn't receive enough information about the patient or image.`}
            mode="light"
            source={modalType}
            title={t`Not enough information.`}
          />
        </ExternalModalLayout>
      );
    }

    if (isErrorPatientData) {
      return (
        <ExternalModalLayout className="vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`The patient information we received didn't match a patient in Detect AI.`}
            mode="light"
            source={modalType}
            title={t`We can't find this patient.`}
          />
        </ExternalModalLayout>
      );
    }

    if (isErrorVisitAndImageData || isErrorTrialStatus) {
      return (
        <ExternalModalLayout className="vai-flex vai-items-center vai-justify-center">
          <ExternalError
            message={t`Please refresh the panel. If the problem continues, contact support for help.`}
            mode="light"
            source={modalType}
            title={t`Trouble loading content.`}
          />
        </ExternalModalLayout>
      );
    }

    if (
      isLoadingPatientData ||
      isLoadingVisitAndImageData ||
      isLoadingTrialStatus
    ) {
      return (
        <ExternalModalLayout className="vai-flex vai-items-center vai-justify-center">
          <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-[3px] vai-border-t-transparent vai-border-sapphire-600 vai-mx-auto" />
        </ExternalModalLayout>
      );
    }

    switch (modalType) {
      case ModalType.PATIENT_EDUCATION:
        return (
          <ExternalModalLayout className="vai-flex vai-flex-col vai-gap-4 vai-p-6">
            <PatientEducationContent
              mode="light"
              imageId={imageIdData?.videaImageId}
            />
          </ExternalModalLayout>
        );
      case ModalType.UPGRADE:
        return (
          <ExternalModalLayout>
            <Upgrade trialStatusData={trialStatusData} />
          </ExternalModalLayout>
        );
      case ModalType.ONBOARDING:
        return (
          <ExternalModalLayout>
            <Onboarding />
          </ExternalModalLayout>
        );
      case ModalType.AI_IMPACT:
        return (
          <ExternalModalLayout>
            <AIImpact />
          </ExternalModalLayout>
        );
      case ModalType.QR_CODE:
      case ModalType.PRINT_DOWNLOAD:
        return (
          <ExternalModalLayout className="!vai-p-0">
            <ExternalQRPrintDownload
              isLoading={isLoadingPatientData || isLoadingVisitAndImageData}
              visitSummaries={visitSummaries}
            />
          </ExternalModalLayout>
        );
      default:
        return (
          <ExternalModalLayout className="vai-flex vai-items-center vai-justify-center">
            <ExternalError
              message={t`Please close the modal and try again`}
              mode="light"
              showButtons={false}
              source={modalType}
              title={t`Something went wrong`}
            />
          </ExternalModalLayout>
        );
    }
  };

  return (
    <ExternalPatientContext.Provider
      value={{
        isIframe: true,
        externalPatientId,
        externalImageId,
        patientId: patientIdData?.patientId ?? null,
        imageId: imageIdData?.videaImageId ?? null,
        trialStatus: trialStatusData?.status ?? null,
        centralizedBilling: trialStatusData?.centralizedBilling ?? null,
      }}
    >
      {modalView()}
    </ExternalPatientContext.Provider>
  );
};
